import { useRef } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { useApp } from "context/app";
import { useSettings } from "context/settings";

import SettingsContentShared from "page_components/shared/settings/Content";

import countries from "libs/countries.json";

import { ReactComponent as LightIcon } from "assets/icons/theme-light.svg";
import { ReactComponent as DarkIcon } from "assets/icons/theme-dark.svg";

const SettingsContent = () => {
  const { theme, setTheme } = useApp();
  const { t, i18n } = useTranslation();

  const { data, updateSettings, setPersonalDataModal, setChangePasswordModal } =
    useSettings();

  const lastLanguage = useRef(null);

  const changeLanguage = async (language) => {
    if (language === (lastLanguage.current || data?.language)) return;
    lastLanguage.current = language;

    try {
      await i18n.changeLanguage(language);
      moment.locale(language);
      await updateSettings(
        { language },
        language === "pl"
          ? t("Language changed to Polish.", { lng: language })
          : t("Language changed to English.", { lng: language }),
      );
    } catch (err) {
      lastLanguage.current = null;
      console.error(err);
    }
  };

  const changeTheme = async (value) => {
    if (value === theme) return;

    try {
      setTheme(value);
      await updateSettings(
        { theme: value },
        value === "dark"
          ? t("Theme changed to dark.")
          : t("Theme changed to light."),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const email = data?.email || "-";
  const phone = !!data?.phone?.number
    ? `${
        countries?.find((item) => item?.code === data?.phone?.code)
          ?.dialling_code || ""
      } ${data?.phone?.number}`
    : "-";

  const columns_data = [
    {
      title: t("User data"),
      action: {
        label: t("Edit"),
        onclick: () => setPersonalDataModal(true),
      },
      items: [
        {
          type: "avatar",
          value: data?.photo,
        },
        {
          label: `${t("Account type")}:`,
          value: "Administrator",
        },
        {
          label: `${t("Full name")}:`,
          value: `${data?.first_name} ${data?.last_name}`,
        },
        {
          type: email !== "-" ? "link" : undefined,
          label: `${t("Email address")}:`,
          value:
            email !== "-"
              ? {
                  link_label: email,
                  link_href: `mailto:${email}`,
                }
              : email,
        },
        {
          type: phone !== "-" ? "link" : undefined,
          label: `${t("Phone number")}:`,
          value:
            phone !== "-"
              ? {
                  link_label: phone,
                  link_href: `tel:${phone.replace(/\s+/g, "")}`,
                }
              : phone,
        },
      ],
      hiddenMobile: true,
    },
    {
      title: t("Password"),
      action: {
        label: t("Change password"),
        onclick: () => setChangePasswordModal(true),
      },
      items: [
        {
          label: t("Last password change"),
          value: moment(data?.last_password_changed)?.fromNow(),
        },
        {
          type: "advice",
          value: t(
            "The password should be at least 10 characters long and include uppercase and lowercase letters, numbers, and special characters such as an exclamation mark. It should be unique for each website and changed periodically every few months. For convenience and increased security, we recommend using a password manager and two-factor authentication, for example through an app or security keys.",
          ),
        },
      ],
      hiddenMobile: true,
    },
    {
      type: "group",
      group: [
        {
          title: t("Panel language"),
          items: [
            {
              type: "radio",
              value: [
                {
                  icon: require("assets/icons/flags/pl.svg").default,
                  label: t("Polish"),
                  value: "pl",
                  selected: data?.language === "pl",
                  onChange: (lang) => changeLanguage(lang),
                },
                {
                  icon: require("assets/icons/flags/en.svg").default,
                  label: t("English"),
                  value: "en",
                  selected: data?.language === "en",
                  onChange: (lang) => changeLanguage(lang),
                },
              ],
            },
          ],
        },
        {
          title: t("Panel appearance"),
          items: [
            {
              type: "radio",
              value: [
                {
                  icon: <LightIcon />,
                  label: t("Light"),
                  value: "light",
                  selected: theme === "light",
                  onChange: (theme) => changeTheme(theme),
                },
                {
                  icon: <DarkIcon />,
                  label: t("Dark"),
                  value: "dark",
                  selected: theme === "dark",
                  onChange: (theme) => changeTheme(theme),
                },
              ],
            },
          ],
        },
      ],
    },
  ];

  const columns_mobile_data = {
    user: {
      header: {
        title: t("User data"),
        action: {
          label: t("Edit"),
          onClick: () => setPersonalDataModal(true),
        },
      },
      content: {
        avatar: data?.photo,
        name: `${data?.first_name} ${data?.last_name} (Administrator)`,
        email,
        phone,
      },
    },
    password: {
      header: {
        title: t("Password"),
      },
      content: {
        last_password_changed: data?.last_password_changed,
        advice: t(
          "The password should be at least 10 characters long and include uppercase and lowercase letters, numbers, and special characters such as an exclamation mark. It should be unique for each website and changed periodically every few months. For convenience and increased security, we recommend using a password manager and two-factor authentication, for example through an app or security keys.",
        ),
      },
      onClick: () => setChangePasswordModal(true),
    },
  };

  return <SettingsContentShared columns_data={columns_data} columns_mobile_data={columns_mobile_data} />;
};

export default SettingsContent;
